// src/Pages/Hmo/InsuranceLanding.js - ENHANCED FALLBACK
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function InsuranceLanding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // ✅ State for params
  const [companyId, setCompanyId] = useState(null);
  const [linkId, setLinkId] = useState(null);
  const [agentId, setAgentId] = useState(null);  // Track separately

  const BRAND_COLOR = '#F89423';
  const BRAND_COLOR_BORDER = '#FDE4C8';
  const BRAND_COLOR_HOVER = '#E0851F';

  // ✅ AGGRESSIVE: Extract agentId from ALL possible sources
  const extractAgentId = () => {
    // Priority 1: URL param
    const fromUrl = searchParams.get('agent');
    if (fromUrl && fromUrl.trim() !== '') {
      console.log('✅ Agent from URL:', fromUrl);
      return fromUrl.trim();
    }
    
    // Priority 2: localStorage with multiple keys
    try {
      const storageKeys = ['agentSession', 'userSession', 'hmoSession'];
      for (const key of storageKeys) {
        const raw = localStorage.getItem(key);
        if (raw) {
          const session = JSON.parse(raw);
          const candidates = [
            session.agentId,
            session.agent_id, 
            session.sessionId,
            session.session_id
          ];
          const found = candidates.find(id => id && id.trim() !== '');
          if (found) {
            console.log(`✅ Agent from localStorage[${key}]:`, found);
            return found.trim();
          }
        }
      }
    } catch (err) {
      console.warn('⚠️ Failed to parse localStorage:', err);
    }
    
    // Priority 3: Referrer URL (if user came from agent's copied link)
    try {
      const referrer = new URL(document.referrer);
      const fromReferrer = referrer.searchParams.get('agent');
      if (fromReferrer && fromReferrer.trim() !== '') {
        console.log('✅ Agent from referrer:', fromReferrer);
        return fromReferrer.trim();
      }
    } catch (err) {
      // Ignore referrer errors
    }
    
    console.warn('❌ AgentId NOT FOUND in any source');
    return null;
  };

  // ✅ Extract ALL params on mount
  useEffect(() => {
    try {
      const company = searchParams.get('company');
      const link = searchParams.get('link');
      const agent = extractAgentId();  // ✅ Use aggressive extractor
      
      console.log('📍 Landing Params Debug:', {
        fromUrl: { company, link, agent: searchParams.get('agent') },
        final: { company, link, agent },
        localStorage: {
          agentSession: localStorage.getItem('agentSession'),
          userSession: localStorage.getItem('userSession')
        }
      });
      
      if (!company || !link) {
        toast.error('Invalid link. Missing company or link parameter.');
        return;
      }
      
      setCompanyId(company);
      setLinkId(link);
      setAgentId(agent);  // ✅ Store final agentId
      
      // ✅ Show visual feedback in dev mode
      if (process.env.NODE_ENV === 'development') {
        toast.info(agent 
          ? `Agent: ${agent.substring(0, 8)}...` 
          : '⚠️ No agent attribution'
        );
      }
      
    } catch (err) {
      console.error('💥 Error extracting params:', err);
      toast.error('Error loading link parameters');
    }
  }, [searchParams]);

  const handleContinue = () => {
    if (!agreed) {
      toast.warning('Please agree to the Privacy Policy to continue');
      return;
    }

    setLoading(true);
    
    setTimeout(() => {
      // ✅ Build params with ALL values including agent
      const params = new URLSearchParams();
      if (companyId) params.set('company', companyId);
      if (linkId) params.set('link', linkId);
      
      // ✅ CRITICAL: Use the agentId we extracted (not searchParams)
      if (agentId) {
        params.set('agent', agentId);
        console.log('✅ Preserving agent attribution:', agentId);
      } else {
        console.warn('⚠️ No agentId to preserve - attribution will be lost');
        // Optional: Try one more fallback before giving up
        const lastResort = extractAgentId();
        if (lastResort) {
          params.set('agent', lastResort);
          console.log('✅ Last-resort agent attribution:', lastResort);
        }
      }
      
      const queryString = params.toString();
      const redirectPath = `/insurance/products?${queryString}`;
      
      console.log('🔗 Navigating to:', redirectPath);
      navigate(redirectPath);
    }, 800);
  };

  const handlePrivacyPolicy = () => {
    toast.info('Privacy Policy page coming soon!');
  };

  const handleClose = () => {
    if (window.opener) {
      window.close();
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-vh-100 d-flex flex-column" style={{ backgroundColor: '#f8f9fa' }}>
      <ToastContainer position="top-center" autoClose={3000} />
      
      {/* Header */}
      <header className="bg-white border-bottom py-3">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <img src="/images/fsl_logo.png" alt="FSL Logo" style={{ height: '40px' }} />
            </div>
            <div className="d-flex gap-3 align-items-center">
              <button className="btn btn-light rounded-circle" style={{ width: '36px', height: '36px' }} onClick={() => toast.info('Help center coming soon!')}>
                <i className="fas fa-question" style={{ color: '#6c757d' }}></i>
              </button>
              <span className="badge bg-light text-dark px-3 py-2">Help</span>
              <button className="btn btn-light rounded-circle" style={{ width: '36px', height: '36px' }} onClick={handleClose}>
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow-1 d-flex align-items-center justify-content-center p-4">
        <div className="rounded-3 shadow-sm" style={{ maxWidth: '500px', width: '100%', backgroundColor: 'white', border: `2px solid ${BRAND_COLOR_BORDER}`, boxShadow: '0 4px 12px rgba(248, 148, 35, 0.1)', padding: '40px 30px' }}>
          <div className="text-center">
            <div className="mb-4">
              <img src="/images/fsl_logo.png" alt="FSL Management Services" style={{ height: '100px', maxWidth: '100%' }} />
            </div>

            <h1 className="fw-bold mb-3" style={{ color: '#111827', fontSize: '26px' }}>Hmo by FSL</h1>
            <p className="text-muted mb-5" style={{ fontSize: '15px' }}>Enjoy all Hmo coverage you need all in one place</p>

            {/* ✅ Agent Attribution Badge */}
            {agentId ? (
              <div className="mb-4">
                <span className="badge" style={{ backgroundColor: `${BRAND_COLOR}15`, color: BRAND_COLOR, border: `1px solid ${BRAND_COLOR_BORDER}`, padding: '6px 12px', fontSize: '12px', fontWeight: '500' }}>
                  <i className="fas fa-user-tag me-1"></i>
                  Agent: {agentId.substring(0, 8)}...
                </span>
              </div>
            ) : (
              process.env.NODE_ENV === 'development' && (
                <div className="mb-4">
                  <span className="badge bg-warning text-dark">
                    <i className="fas fa-exclamation-triangle me-1"></i>
                    No Agent Attribution
                  </span>
                </div>
              )
            )}

            {/* ✅ Debug Info - Dev Only */}
            {process.env.NODE_ENV === 'development' && (
              <div className="mb-4 p-3 bg-light rounded small text-start">
                <div><strong>Debug:</strong></div>
                <div>Company: {companyId || '—'}</div>
                <div>Link: {linkId || '—'}</div>
                <div>Agent: {agentId || 'NOT FOUND ❌'}</div>
                <div className="mt-2">
                  <small className="text-muted">
                    URL agent param: {searchParams.get('agent') || '—'}<br/>
                    Storage keys: {Object.keys(localStorage).filter(k => k.includes('session')).join(', ') || '—'}
                  </small>
                </div>
              </div>
            )}

            {/* Privacy Policy Checkbox */}
            <div className="mb-4">
              <div className="form-check d-inline-flex align-items-center">
                <input className="form-check-input me-2" type="checkbox" id="privacyCheck" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: BRAND_COLOR }} />
                <label className="form-check-label text-muted" htmlFor="privacyCheck" style={{ cursor: 'pointer', fontSize: '14px' }}>
                  I acknowledge and agree to the{' '}
                  <span onClick={handlePrivacyPolicy} style={{ color: BRAND_COLOR, cursor: 'pointer', textDecoration: 'underline' }}>Privacy Policy</span>.
                </label>
              </div>
            </div>

            {/* Continue Button */}
            <button onClick={handleContinue} disabled={loading || !agreed} className="btn w-100 py-3 fw-semibold rounded-3" style={{ backgroundColor: agreed ? BRAND_COLOR : '#9ca3af', color: 'white', border: 'none', transition: 'all 0.3s ease', opacity: agreed ? 1 : 0.6 }} onMouseEnter={(e) => { if (agreed) e.target.style.backgroundColor = BRAND_COLOR_HOVER; }} onMouseLeave={(e) => { if (agreed) e.target.style.backgroundColor = BRAND_COLOR; }}>
              {loading ? (
                <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Loading...</>
              ) : 'Continue'}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="position-fixed bottom-0 mb-3">
          <p className="text-muted small mb-0">
            <i className="fas fa-shield-alt me-1" style={{ color: BRAND_COLOR }}></i>
            Powered by <span style={{ fontWeight: '600', color: BRAND_COLOR }}>FSL</span>
          </p>
        </div>
      </main>
    </div>
  );
}

export default InsuranceLanding;